import type { Point } from './hermite';
import type { PlaneId } from './types';
import { type CocoDataset, emptyCoco } from './coco';

export interface Measurement {
    id: number;
    plane: PlaneId;
    slice: number;
    a: Point;
    b: Point;
    mm: number;
    label: string;
    hidden?: boolean;
}

/** Bounding box as [x, y, width, height], top-left origin. Frac [0,1] in the
 *  runtime {@link Annotation} model; absolute pixels once serialized to COCO. */
export type BBox = [number, number, number, number];

/**
 * The viewer's ergonomic working model for one box: geometry in [0,1] frac
 * coords, scoped to the plane+slice it was drawn on, with a free-text `label`.
 * This is NOT the persisted shape — annotations are stored as a spec-compliant
 * COCO dataset ({@link CocoDataset}); see `toCoco`/`fromCoco` in `coco.ts`.
 */
export interface Annotation {
    id: number;
    plane: PlaneId;
    slice: number;
    bbox: BBox;
    label: string;
    hidden?: boolean;
}

/**
 * Everything the viewer persists for one scan, kept as a SINGLE JSON record
 * (rather than one call per section) so a host can round-trip it as one blob,
 * keyed per scan — hosts that persist sections separately (e.g. one row per
 * kind, see `cbctScanStorage.ts` in files-viewer/dashboard) just assemble/split
 * this shape at their storage boundary. Arch/TMJ/measurement geometry is in
 * [0,1] frac coords; annotations are a full COCO dataset (pixel coords).
 */
export interface ScanRecord {
    arch: Point[]; // validated trace control points
    tmj: { right: Point[]; left: Point[] }; // condyle lines
    measurements: Measurement[];
    annotations: CocoDataset; // spec-compliant COCO detection dataset
}

/**
 * Storage boundary for scan records. The viewer only ever loads the whole
 * record once (before mounting) and pushes section-sized patches on change;
 * every host implements this against its own backend (there's no built-in
 * default — every CBCT asset is backend-persisted, keyed by patientFileId, so
 * there's no case where a host doesn't have one to inject). `patch` is
 * fire-and-forget by design — the viewer never blocks interaction on
 * persistence.
 */
export interface ScanStorage {
    load(scanId: string | undefined): Promise<ScanRecord>;
    patch(scanId: string | undefined, patch: Partial<ScanRecord>): void;
}

const emptyRecord = (): ScanRecord => ({
    arch: [],
    tmj: { right: [], left: [] },
    measurements: [],
    annotations: emptyCoco()
});

/** A fresh all-empty record — the seed when nothing is stored for a scan. */
export const emptyScanRecord = emptyRecord;
